import {Mapper} from '@s-ui/domain'

export class SeoAdsDetailMetaInformationMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Head} deps.seoDataValueObjectFactory
   * @param {SeoDataMapper} deps.seoDataMapper
   * @param {SeoLiteralMapper} seoLiteralMapper
   * @param {VehicleTypeStructuredDataSeoMapper} deps.vehicleTypeStructuredDataSeoMapper
   * @param {BreadcrumbListStructuredDataSeoMapper} deps.breadcrumbListStructuredDataSeoMapper
   * @param {Mapper} deps.fromVehicleParamsToMetaStringMapper
   * @param {Object} deps.config
   * */
  constructor({
    vehicleTypeStructuredDataSeoMapper,
    breadcrumbListStructuredDataSeoMapper,
    seoDataValueObjectFactory,
    seoDataMapper,
    seoLiteralMapper,
    config
  }) {
    super()
    this._seoDataMapper = seoDataMapper
    this._seoLiteralMapper = seoLiteralMapper
    this._config = config
    this._companyUrl = this._config.get('COMPANY_URL')
    this._seoDataValueObjectFactory = seoDataValueObjectFactory
    this._vehicleTypeStructuredDataSeoMapper = vehicleTypeStructuredDataSeoMapper
    this._breadcrumbListStructuredDataSeoMapper = breadcrumbListStructuredDataSeoMapper
  }

  _buildCanonicalUrl = dynamicPath => (dynamicPath ? `${this._companyUrl}${dynamicPath}` : '')

  /**
   * @method
   */
  async map({culture, pageName, params = {}, preloadImages = []}) {
    const {
      bodyType,
      breadcrumb,
      canonical,
      cc,
      color,
      fuelType,
      image,
      kms,
      make,
      model,
      monthlySubscriptionFee,
      offerType,
      offerTypeId,
      price,
      province,
      subscriptionVehicleState,
      title,
      version,
      year,
      adId,
      photos,
      km,
      hp,
      doors,
      seats,
      fuelTankCapacity,
      consumptionAverage,
      emissions,
      transmissionType,
      warrantyMonths,
      hasWarranty,
      hasOfficialWarranty,
      hasStock,
      sellerDealer,
      dealerReviews,
      environmentalLabel
    } = params

    const {ADS_DETAIL} = this._config.get('seo')

    const getSeoType = () => {
      if (monthlySubscriptionFee) return 'subscription'
      if (!make) return 'simple'
      const isSegundaMano = offerType === 'Segunda mano'
      return offerType && !isSegundaMano ? 'offerType' : 'default'
    }
    const seoType = getSeoType()

    const {H1, TITLE, META_DESCRIPTION, META_ROBOTS} = ADS_DETAIL[pageName][seoType][culture]

    const placeholderValues = {
      bodyType,
      cc,
      kms,
      make,
      monthlySubscriptionFee,
      ...(seoType === 'offerType' && {offerType: offerType.toLowerCase()}),
      price,
      province,
      title,
      version,
      year
    }

    const placeholderValuesForDescription = {
      ...placeholderValues,
      ...(bodyType && {bodyType: bodyType.toLowerCase()})
    }

    const parsedCanonical = this._buildCanonicalUrl(canonical)

    const parsedH1 = this._seoLiteralMapper.setText({text: H1}).setPlaceholderValues({placeholderValues}).map()

    const parsedTitle = this._seoLiteralMapper
      .setText({text: TITLE})
      .setPlaceholderValues({
        placeholderValues
      })
      .map()

    const parsedDescription = this._seoLiteralMapper
      .setText({text: META_DESCRIPTION})
      .setPlaceholderValues({
        placeholderValues: placeholderValuesForDescription
      })
      .map()

    const vehicleTypeStructuredDataVO = this._vehicleTypeStructuredDataSeoMapper
      .setParams({
        name: title,
        make: make?.toUpperCase(),
        model: model?.toUpperCase(),
        price,
        url: parsedCanonical,
        image,
        productionDate: year,
        bodyType,
        offerTypeId,
        subscriptionVehicleState,
        fuelType,
        description: parsedDescription,
        color,
        adId,
        photos,
        km,
        cubicCapacity: cc,
        hp,
        doors,
        seats,
        fuelTankCapacity,
        consumptionAverage,
        emissions,
        transmissionType,
        warrantyMonths,
        hasWarranty,
        hasOfficialWarranty,
        hasStock,
        sellerDealer,
        year,
        version,
        dealerReviews,
        companyUrl: this._companyUrl,
        environmentalLabel
      })
      .map()

    const hasBreadcrumb = breadcrumb && breadcrumb.length > 0
    const breadcrumbStructuredDataVO = hasBreadcrumb && this._breadcrumbListStructuredDataSeoMapper.map({breadcrumb})

    const {
      links,
      metas,
      scripts,
      title: seoTitle
    } = this._seoDataMapper
      .title(parsedTitle)
      .description(parsedDescription)
      .robots(META_ROBOTS)
      .canonical(parsedCanonical)
      .setPreloadImages({preloadImages})
      .addSchemaData(vehicleTypeStructuredDataVO)
      .addSchemaData(breadcrumbStructuredDataVO)
      .setOpenGraphData({
        title: parsedTitle,
        description: parsedDescription,
        url: parsedCanonical,
        image
      })
      .setTwitterCardsData()
      .map()

    return this._seoDataValueObjectFactory({
      h1: parsedH1,
      head: {
        link: links,
        meta: metas,
        scripts,
        title: seoTitle
      }
    })
  }
}
